import i18n from "i18next";
import { formatMoneyWithCurrency, formatNumber } from "src/utils/numberUtils.ts";
import { LabelValueView } from "src/components/common/misc/LabelValueView.tsx";
import { calculateSalesMarginPercent } from "src/components/views/erp/sales/salesUtils.ts";
import { RawHtmlContent } from "src/components/common/RawHtmlContent.tsx";
import { TypographyWithInfoCircle } from "src/components/common/typography/TypographyWithInfoCircle.tsx";
import { linebreaksToHtmlBreaks, nullIfBlank } from "src/utils/strings.tsx";
import { PartConfigurationDetailsViewApi_PartConfigurationDto } from "src/api/generated/erp/configurator/api/partConfigurationDetailsViewApi.ts";
import { faBroom } from "@fortawesome/pro-regular-svg-icons";
import { PartConfigurationCachePurgeApi } from "../../../../../api/generated/erp/configurator/api/partConfigurationCachePurgeApi.ts";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AsyncButton } from "../../../../common/buttons/AsyncButton.tsx";
import { PartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/partConfiguratorType.ts";
import { CustomUiActionObjectMenuButton } from "src/components/common/customUiActions/CustomUiActionMenuItems.tsx";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";

export interface PartConfigurationTopLevelDataViewProps {
	partConfiguration: PartConfigurationDetailsViewApi_PartConfigurationDto;
	partConfiguratorType: PartConfiguratorType;
	refreshData: () => Promise<unknown>;
}

export const PartConfigurationTopLevelDataView = ({
	partConfiguration,
	partConfiguratorType,
	refreshData,
}: PartConfigurationTopLevelDataViewProps) => {
	const hasPermission = useHasPermission();
	const domesticCurrencyCode = partConfiguration.domesticCurrencyCode;
	const sellingCurrencyCode = partConfiguration.sellingCurrencyCode ?? null;
	const showCostInSellingCurrency =
		partConfiguration.sellingCurrencyId !== partConfiguration.domesticCurrencyId;

	const marginPercent =
		partConfiguration.totalCostInSellingCurrency == null ? null
		:	calculateSalesMarginPercent({
				cost: partConfiguration.totalCostInSellingCurrency,
				salesPrice: partConfiguration.salesPrice,
			});
	const formatDomesticMoney = (amount: number) => formatMoneyWithCurrency(amount, domesticCurrencyCode);
	const formatSellingMoney = (amount: number | null | undefined) =>
		amount == null || sellingCurrencyCode == null ? null : formatMoneyWithCurrency(amount, sellingCurrencyCode);

	return (
		<VerticalBox
			sx={{
				flex: 1,
				margin: 1,
				overflow: "auto",
				gap: 2,
			}}
		>
			<LabelValueView
				sx={{
					flex: "0 0 auto",
				}}
				items={[
					{
						label: i18n.t("part"),
						value: partConfiguration.partLongName,
					},
					{
						label: i18n.t("description"),
						value: partConfiguration.description,
					},
					nullIfBlank(partConfiguration.customerOrderTypeName) != null && {
						label: i18n.t("customer_order_type"),
						value: partConfiguration.customerOrderTypeName,
					},
					{
						label: i18n.t("material_cost"),
						value: formatDomesticMoney(partConfiguration.materialCost),
					},
					{
						label: i18n.t("labour_share_of_material_costs"),
						value: formatDomesticMoney(partConfiguration.recursiveWorkCost),
					},
					{
						label: i18n.t("labour_cost"),
						value: formatDomesticMoney(partConfiguration.workCost),
					},
					{
						label: i18n.t("total_cost"),
						value: formatDomesticMoney(partConfiguration.totalCost),
					},
					...(sellingCurrencyCode != null ?
						[
							{
								label: i18n.t("calculated_price"),
								value: formatSellingMoney(partConfiguration.basePrice),
							},
							{
								label: i18n.t("discount"),
								value: formatNumber(partConfiguration.discountPercentage) + " %",
							},
							{
								label: i18n.t("sales_price"),
								value: formatSellingMoney(partConfiguration.salesPrice),
							},
							showCostInSellingCurrency && {
								label: i18n.t("total_cost_in_selling_currency"),
								value: formatSellingMoney(partConfiguration.totalCostInSellingCurrency),
							},
							{
								label: i18n.t("profit_margin"),
								value: marginPercent == null ? null : formatNumber(marginPercent, { maxDigits: 1 }) + " %",
							},
						]
					:	[]),
					{
						label: i18n.t("weight"),
						value: partConfiguration.weight != null ? formatNumber(partConfiguration.weight) : null,
					},
					nullIfBlank(partConfiguration.workInstructions) != null && {
						label: i18n.t("work_instructions"),
						value: <RawHtmlContent html={partConfiguration.workInstructions} />,
					},
					nullIfBlank(partConfiguration.printoutText) != null && {
						label: i18n.t("printout_text"),
						value: <RawHtmlContent html={partConfiguration.printoutText} />,
					},
					partConfiguration.defaultProductionLineName != null && {
						label: i18n.t("production_line"),
						value: partConfiguration.defaultProductionLineName,
					},
					{
						label: i18n.t("configuration_id"),
						value: partConfiguration.partConfigurationId,
					},
					{
						label: i18n.t("part_revision"),
						value: (
							<TypographyWithInfoCircle
								infoContent={linebreaksToHtmlBreaks(partConfiguration.partRevisionNote ?? "")}
							>
								{partConfiguration.revisionNumber}
							</TypographyWithInfoCircle>
						),
					},
					{
						label: i18n.t("product_family_version"),
						value: partConfiguration.productFamilyVersionNumber,
					},
					{
						label: i18n.t("log_key"),
						value: partConfiguration.configurationLogPathBase64,
					},
					partConfiguration.configurationContentHash != null && {
						label: i18n.t("configuration_content_hash"),
						value: partConfiguration.configurationContentHash,
					},
				]}
			/>
			<HorizontalBox>
				<CustomUiActionObjectMenuButton
					view={"PART_CONFIGURATIONS"}
					objectIds={[partConfiguration.partConfigurationId]}
					includeZeroCardinalityActions
					refreshData={refreshData}
				/>
				{hasPermission(Permissions.Parts.Configurators.MANAGE) && (
					<AsyncButton
						label={i18n.t("purge_configurator_caches")}
						icon={faBroom}
						variant={"outlined"}
						onClick={async () => {
							await PartConfigurationCachePurgeApi.purgePartConfigurationCache({
								partConfigurationId: partConfiguration.partConfigurationId,
								configuratorType: partConfiguratorType,
							});
						}}
					/>
				)}
			</HorizontalBox>
		</VerticalBox>
	);
};
